import { useState } from "react";
import { submitFeedback } from "../api";

const CONTACT_EMAIL = "contact@gufgaff.com";

const TYPES = [
  { value: "bug",     label: "Bug report" },
  { value: "feature", label: "Feature request" },
  { value: "general", label: "General feedback" },
];

const field = {
  background: "#111113",
  border: "0.5px solid #252528",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 13,
  color: "#d0d0d8",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
};

export default function FeedbackPage() {
  const [form,    setForm]    = useState({ name: "", email: "", type: "general", message: "" });
  const [busy,    setBusy]    = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState("");

  function set(key, val) {
    setForm(f => ({ ...f, [key]: val }));
    setError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.message.trim()) { setError("Message is required."); return; }
    setBusy(true);
    setError("");
    try {
      const res = await submitFeedback({
        name:    form.name.trim()    || null,
        email:   form.email.trim()   || null,
        type:    form.type,
        message: form.message.trim(),
      });
      if (res.success) {
        setSuccess(true);
      } else {
        setError(res.error || "Something went wrong — please try again.");
      }
    } catch {
      setError("Network error — please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#09090b", padding: "48px 20px" }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 10, color: "#FF5C35", fontWeight: 600,
              fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "0.08em" }}>
              BETA FEEDBACK
            </span>
          </div>
          <h1 style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 30, fontWeight: 800, color: "#f2f2f4",
            letterSpacing: "-0.8px", lineHeight: 1.1, marginBottom: 10,
          }}>
            Help us build GuffGaff
          </h1>
          <p style={{
            fontSize: 13, color: "#6a6a7a", lineHeight: 1.7,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}>
            Found a bug? Have an idea? Just want to say something? We read every single message.
            You can also reach us directly at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "#FF5C35", textDecoration: "none" }}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </div>

        {success ? (
          <div style={{
            background: "#0e1e12", border: "0.5px solid #1e4028",
            borderRadius: 14, padding: "32px 28px", textAlign: "center",
          }}>
            <div style={{ fontSize: 36, marginBottom: 14 }}>🙏</div>
            <div style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: 18, fontWeight: 800, color: "#4aaa70",
              marginBottom: 8,
            }}>
              Thanks for your feedback!
            </div>
            <p style={{
              fontSize: 13, color: "#6a8a74", lineHeight: 1.7,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
              We read every message and it directly shapes what we build next.
            </p>
            <button
              onClick={() => { setSuccess(false); setForm({ name: "", email: "", type: "general", message: "" }); }}
              style={{
                marginTop: 20, padding: "8px 20px", borderRadius: 20,
                border: "0.5px solid #1e4028", background: "transparent",
                color: "#4aaa70", fontSize: 12, cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{
            background: "#111113", border: "0.5px solid #252528",
            borderRadius: 14, padding: "28px 24px",
            display: "flex", flexDirection: "column", gap: 16,
          }}>

            {/* Name + Email row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 11, color: "#55555e",
                  fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 6, fontWeight: 600 }}>
                  Name <span style={{ color: "#3a3a44" }}>(optional)</span>
                </label>
                <input
                  value={form.name}
                  onChange={e => set("name", e.target.value)}
                  placeholder="your name"
                  style={field}
                  onFocus={e => { e.target.style.borderColor = "#FF5C35"; }}
                  onBlur={e => { e.target.style.borderColor = "#252528"; }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, color: "#55555e",
                  fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 6, fontWeight: 600 }}>
                  Email <span style={{ color: "#3a3a44" }}>(optional)</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => set("email", e.target.value)}
                  placeholder="so we can reply"
                  style={field}
                  onFocus={e => { e.target.style.borderColor = "#FF5C35"; }}
                  onBlur={e => { e.target.style.borderColor = "#252528"; }}
                />
              </div>
            </div>

            {/* Type */}
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#55555e",
                fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 6, fontWeight: 600 }}>
                Type
              </label>
              <select
                value={form.type}
                onChange={e => set("type", e.target.value)}
                style={{
                  ...field,
                  appearance: "none", WebkitAppearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2355555e'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  paddingRight: 32,
                  cursor: "pointer",
                }}
                onFocus={e => { e.target.style.borderColor = "#FF5C35"; }}
                onBlur={e => { e.target.style.borderColor = "#252528"; }}
              >
                {TYPES.map(t => (
                  <option key={t.value} value={t.value} style={{ background: "#111113" }}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#55555e",
                fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 6, fontWeight: 600 }}>
                Message <span style={{ color: "#FF5C35" }}>*</span>
              </label>
              <textarea
                value={form.message}
                onChange={e => set("message", e.target.value)}
                placeholder="Tell us what's on your mind…"
                rows={5}
                required
                style={{
                  ...field,
                  resize: "vertical",
                  lineHeight: 1.65,
                  minHeight: 110,
                }}
                onFocus={e => { e.target.style.borderColor = "#FF5C35"; }}
                onBlur={e => { e.target.style.borderColor = "#252528"; }}
              />
            </div>

            {error && (
              <div style={{
                fontSize: 12, color: "#cc4444", padding: "8px 12px",
                background: "#1a0808", borderRadius: 8, border: "0.5px solid #3a1a1a",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>{error}</div>
            )}

            <button
              type="submit"
              disabled={busy}
              style={{
                padding: "11px", borderRadius: 10, border: "none",
                background: busy ? "#552010" : "linear-gradient(135deg, #FF5C35, #ff7a58)",
                color: "#fff", fontSize: 13, fontWeight: 700,
                fontFamily: "'Bricolage Grotesque', sans-serif",
                cursor: busy ? "default" : "pointer",
                letterSpacing: "0.01em",
                transition: "opacity 0.15s",
                boxShadow: busy ? "none" : "0 2px 14px rgba(255,92,53,0.25)",
              }}
            >
              {busy ? "Sending…" : "Send feedback"}
            </button>

          </form>
        )}

        {/* Direct contact */}
        <div style={{
          marginTop: 24, padding: "16px 20px",
          background: "#0d0d0f", borderRadius: 10,
          border: "0.5px solid #1c1c1f",
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 18 }}>✉️</span>
          <div>
            <div style={{ fontSize: 12, color: "#9898a6",
              fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 2 }}>
              Prefer email?
            </div>
            <a href={`mailto:${CONTACT_EMAIL}`} style={{
              fontSize: 13, color: "#FF5C35", fontWeight: 600,
              fontFamily: "'Plus Jakarta Sans', sans-serif", textDecoration: "none",
            }}>
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
